import React, { useRef } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
} from 'motion/react';
import './Dock.css';

export interface DockItemData {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  href?: string;
  badge?: string | number;
}

interface DockProps {
  items: DockItemData[];
  className?: string;
  magnification?: number;
  distance?: number;
}

function DockIcon({
  item,
  mouseX,
  magnification = 56,
  distance = 140,
}: {
  item: DockItemData;
  mouseX: MotionValue<number>;
  magnification?: number;
  distance?: number;
}) {
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const distanceCalc = useTransform(mouseX, (val: number) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  const widthSync = useTransform(
    distanceCalc,
    [-distance, 0, distance],
    [40, magnification, 40]
  );

  const width = useSpring(widthSync, {
    mass: 0.1,
    stiffness: 160,
    damping: 14,
  });

  const content = (
    <>
      <span className="dock-tooltip">{item.label}</span>
      <div className="dock-icon-inner">{item.icon}</div>
      {item.badge !== undefined && (
        <span className="dock-badge">{item.badge}</span>
      )}
    </>
  );

  if (item.href) {
    return (
      <motion.a
        ref={ref as React.RefObject<HTMLAnchorElement>}
        href={item.href}
        style={{ width, height: width }}
        className="dock-item"
        onClick={item.onClick}
        aria-label={item.label}
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      ref={ref as React.RefObject<HTMLButtonElement>}
      type="button"
      style={{ width, height: width }}
      className="dock-item"
      onClick={item.onClick}
      aria-label={item.label}
    >
      {content}
    </motion.button>
  );
}

export function Dock({
  items,
  className = '',
  magnification = 54,
  distance = 130,
}: DockProps) {
  const mouseX = useMotionValue(Infinity);

  return (
    <motion.nav
      className={`dock-container ${className}`}
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      aria-label="Quick Actions Dock"
    >
      <div className="dock-panel">
        {items.map((item) => (
          <DockIcon
            key={item.id}
            item={item}
            mouseX={mouseX}
            magnification={magnification}
            distance={distance}
          />
        ))}
      </div>
    </motion.nav>
  );
}
